import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../config/axiosInstance";
import { API } from "../config/config";
import { useProfile } from "../context/ProfileContext";
import UserRideAnalytics from "../components/UserRideAnalytics";
import {
  FaUser,
  FaChartBar,
  FaPhone,
  FaEnvelope,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaSpinner,
} from "react-icons/fa";

const UserDetail = () => {
  const { profile } = useProfile();
  const isSuperAdmin =
    profile?.role?.name === "SuperAdmin" ||
    profile?.role?.slug === "superadmin" ||
    profile?.email === "superadmin@udrivo.in";
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("profile");
  const [testRecordUpdating, setTestRecordUpdating] = useState(false);
  const { id } = useParams();

  const formatINRAmount = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(0);
    }

    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("adminToken");

      const response = await axiosInstance.get(
        `${API.USER.USER_DETAILS}/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = response.data.data || response.data.user || response.data;
      if (!data) {
        setUser(null);
        return;
      }

      // Normalize nested API response for Contact Information and User Basic Info cards
      const pd = data.personalDetails || {};
      const ai = data.accountInfo || {};
      const ld = data.locationDetails || {};
      const rs = data.rideStatistics || {};
      const normalized = {
        _id: data._id || data.userId,
        userId: data.userId,
        fullName: pd.fullName ?? data.fullName,
        phone: pd.phone ?? data.phone,
        email: pd.email ?? data.email,
        profileImage: pd.profileImage,
        createdAt: ai.joinDate,
        updatedAt: ai.lastUpdated,
        status: ai.status,
        isTestRecord: data.isTestRecord,
        totalRides: rs.totalRides,
        completedRides: rs.completedRides ?? data.completedRides,
        totalSpent: rs.totalSpent,
        rating: pd.overallRating?.value ?? data.overallRating?.value,
        city: ld.city,
        state: ld.state,
        address: ld.address,
        location: ld.location,
        carInformation: data.carInformation,
        rideStatistics: data.rideStatistics,
        accountInfo: data.accountInfo,
        personalDetails: data.personalDetails,
        locationDetails: data.locationDetails,
      };
      setUser(normalized);
    } catch (error) {
      console.error("Error fetching user details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserDetails();
  }, [id]);

  const handleToggleTestRecord = async () => {
    try {
      if (!user?._id) return;
      setTestRecordUpdating(true);
      const token = localStorage.getItem("adminToken");
      const nextValue = !Boolean(user.isTestRecord);

      const response = await axiosInstance.patch(
        API.ADMIN.USER_TEST_RECORD(user._id),
        { isTestRecord: nextValue },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response?.data?.success) {
        setUser((prev) => (prev ? { ...prev, isTestRecord: nextValue } : prev));
        toast.success(nextValue ? "Test record enabled" : "Test record disabled");
      } else {
        toast.error(response?.data?.message || "Failed to update test record status");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update test record status"
      );
    } finally {
      setTestRecordUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 py-10 px-6">
        <div className="flex flex-col justify-center items-center py-20">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
            <FaUser className="text-6xl text-purple-500 mb-4 mx-auto" />
            <FaSpinner className="animate-spin text-3xl text-purple-500 mb-4 mx-auto" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Loading User Details
            </h2>
            <p className="text-gray-600 mb-4">
              Please wait while we fetch user information...
            </p>
            <div className="flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"></div>
              <div
                className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"
                style={{ animationDelay: "0.1s" }}
              ></div>
              <div
                className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"
                style={{ animationDelay: "0.2s" }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-20">
        <div className="text-gray-500 text-lg">User not found.</div>
        <button
          onClick={fetchUserDetails}
          className="mt-4 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  const tabs = [
    {
      id: "profile",
      label: "User Profile",
      icon: FaUser,
    },
    {
      id: "analytics",
      label: "Ride Analytics",
      icon: FaChartBar,
    },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "profile":
        return (
          <div className="space-y-6">
            {/* User Basic Info Card */}
            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                    {user.profileImage ? (
                      <img
                        src={user.profileImage}
                        alt={user.fullName}
                        className="w-16 h-16 rounded-full object-cover"
                      />
                    ) : (
                      <FaUser className="text-2xl text-purple-600" />
                    )}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800">
                      {user.fullName || user.name || "Unknown User"}
                    </h2>
                    <p className="text-gray-600">User ID: {id}</p>
                    <div className="flex items-center space-x-4 mt-2">
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-medium ${
                          user.status === "Active"
                            ? "bg-green-100 text-green-800"
                            : user.status === "Verified"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {user.status || "Unknown"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FaUser className="mr-2 text-purple-600" />
                Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-gray-600 flex items-center">
                    <FaPhone className="mr-2" />
                    Phone Number
                  </label>
                  <p className="text-gray-800 mt-1">
                    {user.phone ||
                      user.contactNumber ||
                      user.phoneNumber ||
                      "Not provided"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600 flex items-center">
                    <FaEnvelope className="mr-2" />
                    Email Address
                  </label>
                  <p className="text-gray-800 mt-1">
                    {user.email || "Not provided"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600 flex items-center">
                    <FaCalendarAlt className="mr-2" />
                    Member Since
                  </label>
                  <p className="text-gray-800 mt-1">
                  {user.createdAt
                        ? new Date(user.createdAt).toLocaleString("en-IN", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                            second: "2-digit",
                            hour12: true,
                          })
                        : "Not available"}
                  </p>
                </div>
                {/* <div>
                  <label className="text-sm font-medium text-gray-600 flex items-center">
                    <FaMapMarkerAlt className="mr-2" />
                    Location
                  </label>
                  <p className="text-gray-800 mt-1">
                    {user.city && user.state
                      ? `${user.city}, ${user.state}`
                      : user.location || "Not provided"}
                  </p>
                </div> */}
              </div>
            </div>

            {/* Account Information */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Account Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">
                    {user?.rideStatistics?.completedRides ??
                      user?.completedRides ??
                      0}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Total Completed Rides</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {formatINRAmount(
                      user?.rideStatistics?.totalSpent ?? user.totalSpent ?? 0
                    )}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Total Paid</div>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">
                    {user.rating || "N/A"}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Average Rating
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Details */}
            {(user.dateOfBirth || user.gender || user.emergencyContact) && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Personal Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {user.dateOfBirth && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        Date of Birth
                      </label>
                      <p className="text-gray-800 mt-1">
                        {new Date(user.dateOfBirth).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  {user.gender && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        Gender
                      </label>
                      <p className="text-gray-800 mt-1 capitalize">
                        {user.gender}
                      </p>
                    </div>
                  )}
                  {user.emergencyContact && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        Emergency Contact
                      </label>
                      <p className="text-gray-800 mt-1">
                        {user.emergencyContact}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      case "analytics":
        return (
          <div className="p-6">
            <UserRideAnalytics userId={id} user={user} />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-10 px-6">
      <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-8 text-white">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              {user.profileImage ? (
                <img
                  src={user.profileImage}
                  alt={user.fullName}
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <FaUser className="text-2xl text-white" />
              )}
            </div>
            <div>
              <h1 className="text-3xl font-bold">
                {user.fullName || user.name || "Unknown User"}
              </h1>
              <p className="text-purple-100">
                Customer since{" "}
                {user.createdAt
                  ? new Date(user.createdAt).toLocaleDateString()
                  : "Unknown"}
              </p>
            </div>
          </div>
        </div>

        {isSuperAdmin && (
          <div className="px-6 pt-4">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Test Record Control</h3>
                <p className="text-xs text-gray-600 mt-1">
                  Mark this user as test data to exclude from revenue metrics.
                </p>
                <span
                  className={`inline-flex mt-2 px-2 py-1 text-xs font-medium rounded-full ${
                    user.isTestRecord
                      ? "bg-rose-100 text-rose-800"
                      : "bg-blue-100 text-blue-800"
                  }`}
                >
                  {user.isTestRecord ? "Enabled (True)" : "Disabled (False)"}
                </span>
              </div>

              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={Boolean(user.isTestRecord)}
                  onChange={handleToggleTestRecord}
                  disabled={testRecordUpdating}
                  className="sr-only peer"
                />
                <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-2 peer-focus:ring-indigo-300 peer-checked:bg-indigo-600 transition-colors">
                  <span className="absolute top-[2px] left-[2px] h-5 w-5 rounded-full bg-white transition-transform peer-checked:translate-x-5" />
                </div>
                <span className="ml-3 text-sm font-medium text-gray-700">
                  {testRecordUpdating
                    ? "Updating..."
                    : user.isTestRecord
                    ? "ON"
                    : "OFF"}
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? "border-purple-500 text-purple-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="min-h-[600px]">{renderTabContent()}</div>
      </div>
    </div>
  );
};

export default UserDetail;
